/**
 * remark plugin: turns file-path-looking substrings in chat message text
 * into markdown `link` nodes carrying a synthetic `taskin-file-candidate:`
 * URL, so `ChatMarkdown`'s existing `a` renderer can pick them up the same
 * way it already handles real markdown links.
 *
 * Deliberately walks the mdast tree by hand instead of pulling in
 * `unist-util-visit`: the real `mdast`/`unist` type packages aren't a
 * resolvable dependency of this workspace (only present nested inside other
 * packages' own installs), so this uses a minimal local structural type for
 * exactly the shape it touches, kept in sync with the `mdast` node names by
 * hand rather than by import.
 */
import { extractPreviewablePaths } from "./lib/pathCandidates";

const FILE_CANDIDATE_SCHEME = "taskin-file-candidate:";

export function encodeFileCandidateHref(path: string): string {
  return `${FILE_CANDIDATE_SCHEME}${encodeURIComponent(path)}`;
}

export function decodeFileCandidateHref(href: string): string | null {
  if (!href.startsWith(FILE_CANDIDATE_SCHEME)) return null;
  try {
    return decodeURIComponent(href.slice(FILE_CANDIDATE_SCHEME.length));
  } catch {
    return null;
  }
}

interface MdastNode {
  readonly type: string;
  value?: string;
  /** Only present on `link` nodes. */
  url?: string;
  children?: MdastNode[];
}

/** Node types whose subtree already has its own link/code handling — never
 * split text inside them, so an existing markdown link or fenced code block
 * keeps its exact content. */
const SKIP_SUBTREE_TYPES = new Set(["link", "linkReference", "code", "html"]);

function splitTextNode(node: MdastNode, allowedGlobs: readonly string[]): MdastNode[] {
  const candidates = extractPreviewablePaths(node.value ?? "", allowedGlobs);
  if (candidates.length === 0) return [node];

  const value = node.value ?? "";
  const parts: MdastNode[] = [];
  let cursor = 0;
  for (const candidate of candidates) {
    if (candidate.start > cursor) {
      parts.push({ type: "text", value: value.slice(cursor, candidate.start) });
    }
    parts.push({
      type: "link",
      url: encodeFileCandidateHref(candidate.text),
      children: [{ type: "text", value: candidate.text }],
    });
    cursor = candidate.end;
  }
  if (cursor < value.length) {
    parts.push({ type: "text", value: value.slice(cursor) });
  }
  return parts;
}

/**
 * Only linkifies when the entire inline-code span is one path candidate —
 * a partial match (e.g. `npm run build:path/to/x`) would otherwise drop the
 * surrounding code formatting for text that was not meant as a file link.
 */
function linkifyInlineCode(node: MdastNode, allowedGlobs: readonly string[]): MdastNode {
  const value = node.value ?? "";
  const candidates = extractPreviewablePaths(value, allowedGlobs);
  const [only] = candidates;
  if (candidates.length !== 1 || !only || only.start !== 0 || only.end !== value.length) {
    return node;
  }
  return {
    type: "link",
    url: encodeFileCandidateHref(only.text),
    children: [{ type: "inlineCode", value }],
  };
}

function walk(node: MdastNode, allowedGlobs: readonly string[]): void {
  if (!node.children) return;
  const next: MdastNode[] = [];
  for (const child of node.children) {
    if (SKIP_SUBTREE_TYPES.has(child.type)) {
      next.push(child);
      continue;
    }
    if (child.type === "text") {
      next.push(...splitTextNode(child, allowedGlobs));
      continue;
    }
    if (child.type === "inlineCode") {
      next.push(linkifyInlineCode(child, allowedGlobs));
      continue;
    }
    walk(child, allowedGlobs);
    next.push(child);
  }
  node.children = next;
}

export interface RemarkFilePathCandidatesOptions {
  /** The project's effective file-preview allowlist. Empty means this plugin
   * does nothing at all — an install with no configured locations shows no
   * file links in chat. */
  readonly allowedGlobs: readonly string[];
}

/**
 * A remark plugin factory — pass it with its options in `remarkPlugins`:
 * `[[remarkFilePathCandidates, { allowedGlobs }]]`.
 */
export function remarkFilePathCandidates(options: RemarkFilePathCandidatesOptions) {
  const allowedGlobs = options.allowedGlobs;
  return (tree: MdastNode) => {
    if (allowedGlobs.length === 0) return;
    walk(tree, allowedGlobs);
  };
}
